/**
 * RBAC utility – single source of truth for access-level checks on the frontend.
 *
 * Roles:
 *   super_admin        (Level 1: Full Access)
 *   branch_admin       (Level 2: Branch Scoped)
 *   batch_admin        (Level 3: Batch Scoped)
 *   course_admin       (Level 4: Course Scoped)
 *   content_manager    (Level 5: Content Scoped)
 *   inquiry_admin      (Level 6: Inquiry Scoped)
 *   attendance_manager (Level 7: Attendance Scoped)
 *   faculty            (Level 8: Faculty Scoped)
 *   viewer             (Level 9: Read Only)
 */

export function getProfile() {
    try {
        return JSON.parse(localStorage.getItem('admin_profile') || '{}');
    } catch {
        return {};
    }
}

/** Legacy support for numeric access levels */
export function getAccessLevel() {
    const profile = getProfile();
    if (profile.access_level) return profile.access_level;

    const roleMap = {
        'super_admin': 1,
        'branch_admin': 2,
        'batch_admin': 3,
        'course_admin': 4,
        'content_manager': 5,
        'inquiry_admin': 6,
        'attendance_manager': 7,
        'faculty': 8,
        'viewer': 9,
        'doubt_expert': 10
    };
    return roleMap[profile.role] ?? 99;
}

export function getRole() {
    const profile = getProfile();
    if (profile.role) return profile.role;
    
    // Map access_level back to role names
    const levelToRole = {
        1: 'super_admin',
        2: 'branch_admin',
        3: 'batch_admin',
        4: 'course_admin',
        5: 'content_manager',
        6: 'inquiry_admin',
        7: 'attendance_manager',
        8: 'faculty',
        9: 'viewer',
        10: 'doubt_expert'
    };
    
    return levelToRole[profile.access_level] ?? 'viewer';
}

/** True when the logged-in user's level is ≤ maxLevel. */
export function canAccess(maxLevel) {
    return getAccessLevel() <= maxLevel;
}

/** True when the user has a specific role or higher. */
export function hasRole(requiredRole) {
    const roleMap = {
        'super_admin': 1,
        'branch_admin': 2,
        'batch_admin': 3,
        'course_admin': 4,
        'content_manager': 5,
        'inquiry_admin': 6,
        'attendance_manager': 7,
        'faculty': 8,
        'viewer': 9,
        'doubt_expert': 10
    };
    return roleMap[getRole()] <= roleMap[requiredRole];
}

/** True when the user can edit a given entity scope. */
export const rbac = {
    // Master Data
    canEditBranches: () => hasRole('super_admin'),
    canEditBatches: () => hasRole('branch_admin'),
    canEditCourses: () => hasRole('super_admin'),
    canEditFaculty: () => hasRole('super_admin'),
    canEditRooms: () => hasRole('branch_admin'),

    // Assignments
    canEditAssignments: () => hasRole('super_admin'),

    // Calendar & Scheduling
    canRunAlgorithm: () => hasRole('super_admin'),
    canPublish: () => hasRole('super_admin'),
    canEditEvents: () => hasRole('faculty'),
    isViewOnly: () => getRole() === 'viewer',

    // Settings / Admin
    canManageAdmins: () => hasRole('super_admin'),
    canManageSettings: () => hasRole('super_admin'),
    canDeleteEvents: () => hasRole('branch_admin'),

    // Panel visibility
    canSeeScheduling: () => hasRole('super_admin'),
    canSeeEntities: () => hasRole('branch_admin'),
    canSeeAssignments: () => hasRole('super_admin'),

    // ScholarsAnchor
    canManageDoubtQueries: () => hasRole('doubt_expert'),
};

/**
 * Validates that an event payload is safe to send to Supabase
 */
export function validateEventPayload(payload) {
    const errors = [];
    if (!payload.title || payload.title.length < 3) errors.push('Title must be at least 3 characters');
    if (!payload.start_time || !payload.end_time) errors.push('Start and end times are required');
    if (new Date(payload.start_time) >= new Date(payload.end_time)) errors.push('End time must be after start time');
    if (!payload.team_id) errors.push('Security error: Team context lost. Please re-login.');
    return { valid: errors.length === 0, errors };
}

/**
 * Check whether the current user can edit a specific calendar event.
 * Scoped based on role.
 */
export function canEditEvent(event) {
    const profile = getProfile();
    const role = getRole();

    if (role === 'super_admin') return true;
    if (role === 'viewer') return false;

    // branch_admin – can edit events belonging to their branch
    if (role === 'branch_admin') {
        return event.branch_id === profile.branch_id;
    }

    // batch_admin – can edit events belonging to their assigned batch
    if (role === 'batch_admin') {
        return event.batch_id === profile.batch_id;
    }

    // faculty – can edit events assigned to them
    if (role === 'faculty') {
        return event.faculty_id === profile.faculty_id;
    }

    return false;
}
